<template>
    <div class="ai-chat-conversation-list flex flex-col flex-1 overflow-hidden">
        <div class="px-3 pt-2 pb-1">
            <div class="relative">
                <i
                    class="far fa-search text-muted absolute top-1/2 transform -translate-y-1/2 text-xs"
                    style="left: 0.75rem;"
                ></i>
                <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search conversations..."
                    class="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                >
            </div>
        </div>

        <div
            v-if="isLoading"
            class="flex items-center justify-center py-8"
        >
            <i class="far fa-spinner-third fa-spin text-blue-500 text-xl"></i>
        </div>

        <perfect-scrollbar
            v-else-if="filteredConversations.length"
            class="flex-1 px-3 py-2"
        >
            <div
                v-for="conversation in filteredConversations"
                :key="conversation.id"
                class="conversation-card group border border-gray-400 rounded-lg mb-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                @click="$emit('select', conversation.id)"
            >
                <div class="px-3 py-3">
                    <div class="flex items-start justify-between">
                        <div class="flex items-center min-w-0 flex-1">
                            <i class="far fa-message-lines text-blue-500 mr-2 flex-shrink-0"></i>
                            <span class="text-sm font-semibold text-gray-800 truncate">
                                {{ conversation.title || 'New Conversation' }}
                            </span>
                        </div>
                        <button
                            class="ml-2 text-muted hover:text-red-500 flex-shrink-0 invisible group-hover:visible"
                            @click.stop="$emit('delete', conversation.id)"
                        >
                            <i class="far fa-trash-alt text-xs"></i>
                        </button>
                    </div>
                    <p
                        v-if="conversation.last_message_preview"
                        class="text-xs text-muted mt-1.5 truncate pl-5"
                    >
                        {{ conversation.last_message_preview }}
                    </p>
                    <div class="text-xs text-muted mt-1 pl-5">
                        {{ formatDate(conversation.last_message_at || conversation.created_at) }}
                    </div>
                </div>
            </div>
        </perfect-scrollbar>

        <div
            v-else-if="searchQuery && conversations.length"
            class="flex flex-col items-center justify-center py-12 px-6 text-center"
        >
            <i class="far fa-search text-gray-300 text-3xl mb-3"></i>
            <p class="text-muted text-sm">
                No conversations match "{{ searchQuery }}"
            </p>
        </div>

        <div
            v-else
            class="flex flex-col items-center justify-center py-16 px-6 text-center"
        >
            <div class="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                <i class="far fa-messages text-blue-400 text-2xl"></i>
            </div>
            <p class="text-gray-600 font-medium text-sm">
                No conversations yet
            </p>
            <p class="text-muted text-xs mt-1">
                Type a message below to get started
            </p>
        </div>
    </div>
</template>

<script>
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

export default {
    components: {
        PerfectScrollbar,
    },

    props: {
        conversations: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            searchQuery: '',
        };
    },

    computed: {
        filteredConversations() {
            if (!this.searchQuery) return this.conversations;

            const query = this.searchQuery.toLowerCase();

            return this.conversations.filter((c) => {
                const title = (c.title || '').toLowerCase();
                const preview = (c.last_message_preview || '').toLowerCase();

                return title.includes(query) || preview.includes(query);
            });
        },
    },

    methods: {
        formatDate(dateString) {
            if (!dateString) return '';

            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;

            return date.toLocaleDateString();
        },
    },
};
</script>
